import { createSlice } from "@reduxjs/toolkit";
import {
    fetchSubSubCategoriesThunk,
    createSubSubCategoryThunk,
    updateSubSubCategoryThunk,
    toggleSubSubcategoriestatusThunk,
    fetchMainCategoriesThunk,
    createMainCategoryThunk,
    fetchSubCategoriesThunk,
    createSubCategoryThunk,
    toggleMainCategoryTreeStatusThunk,
    toggleSubCategoryTreeStatusThunk
} from "./categoriesThunk";

const initialState = {
    // Main categories
    mainCategories: {
        items: [],
        loading: false,
        error: null,
        actionStatus: {
            create: { loading: false, error: null, success: false }
        }
    },
    // Sub categories
    subCategories: {
        items: [],
        loading: false,
        error: null,
        actionStatus: {
            create: { loading: false, error: null, success: false }
        }
    },
    // Sub-sub categories
    subSubCategories: {
        items: [],
        loading: false,
        error: null,
        actionStatus: {
            create: { loading: false, error: null, success: false },
            update: { loading: false, error: null, success: false },
            toggle: { loading: false, error: null, success: false }
        }
    }
};

const categoriesSlice = createSlice({
    name: "categories",
    initialState,
    reducers: {
        resetActionStatus: (state) => {
            state.subSubCategories.actionStatus = {
                create: { loading: false, error: null, success: false },
                update: { loading: false, error: null, success: false },
                toggle: { loading: false, error: null, success: false }
            };
            state.mainCategories.actionStatus = {
                create: { loading: false, error: null, success: false }
            };
            state.subCategories.actionStatus = {
                create: { loading: false, error: null, success: false }
            };
        },
        resetSubSubCategoriesActionStatus: (state) => {
            state.subSubCategories.actionStatus = {
                create: { loading: false, error: null, success: false },
                update: { loading: false, error: null, success: false },
                toggle: { loading: false, error: null, success: false }
            };
        },
        resetMainCategoriesActionStatus: (state) => {
            state.mainCategories.actionStatus = {
                create: { loading: false, error: null, success: false }
            };
        },
        resetSubCategoriesActionStatus: (state) => {
            state.subCategories.actionStatus = {
                create: { loading: false, error: null, success: false }
            };
        },
        clearSubCategories: (state) => {
            state.subCategories.items = [];
            state.subCategories.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // Main fetch
            .addCase(fetchMainCategoriesThunk.pending, (state) => {
                state.mainCategories.loading = true;
                state.mainCategories.error = null;
            })
            .addCase(fetchMainCategoriesThunk.fulfilled, (state, action) => {
                state.mainCategories.loading = false;
                state.mainCategories.items = action.payload;
            })
            .addCase(fetchMainCategoriesThunk.rejected, (state, action) => {
                state.mainCategories.loading = false;
                state.mainCategories.error = action.payload || "Failed to fetch main categories";
            })

            // Main create
            .addCase(createMainCategoryThunk.pending, (state) => {
                state.mainCategories.actionStatus.create.loading = true;
                state.mainCategories.actionStatus.create.error = null;
                state.mainCategories.actionStatus.create.success = false;
            })
            .addCase(createMainCategoryThunk.fulfilled, (state, action) => {
                state.mainCategories.actionStatus.create.loading = false;
                state.mainCategories.actionStatus.create.success = true;
                state.mainCategories.items = [...state.mainCategories.items, action.payload];
            })
            .addCase(createMainCategoryThunk.rejected, (state, action) => {
                state.mainCategories.actionStatus.create.loading = false;
                state.mainCategories.actionStatus.create.error =
                    action.payload || "Failed to create main category";
                state.mainCategories.actionStatus.create.success = false;
            })

            // Sub fetch
            .addCase(fetchSubCategoriesThunk.pending, (state) => {
                state.subCategories.loading = true;
                state.subCategories.error = null;
            })
            .addCase(fetchSubCategoriesThunk.fulfilled, (state, action) => {
                state.subCategories.loading = false;
                state.subCategories.items = action.payload;
            })
            .addCase(fetchSubCategoriesThunk.rejected, (state, action) => {
                state.subCategories.loading = false;
                state.subCategories.error = action.payload || "Failed to fetch sub categories";
            })

            // Sub Create
            .addCase(createSubCategoryThunk.pending, (state) => {
                state.subCategories.actionStatus.create.loading = true;
                state.subCategories.actionStatus.create.error = null;
                state.subCategories.actionStatus.create.success = false;
            })
            .addCase(createSubCategoryThunk.fulfilled, (state, action) => {
                state.subCategories.actionStatus.create.loading = false;
                state.subCategories.actionStatus.create.success = true;
                state.subCategories.items = [...state.subCategories.items, action.payload];
            })
            .addCase(createSubCategoryThunk.rejected, (state, action) => {
                state.subCategories.actionStatus.create.loading = false;
                state.subCategories.actionStatus.create.error =
                    action.payload || "Failed to create sub category";
                state.subCategories.actionStatus.create.success = false;
            })

            // Sub-sub fetch
            .addCase(fetchSubSubCategoriesThunk.pending, (state) => {
                state.subSubCategories.loading = true;
                state.subSubCategories.error = null;
            })
            .addCase(fetchSubSubCategoriesThunk.fulfilled, (state, action) => {
                state.subSubCategories.loading = false;
                state.subSubCategories.items = action.payload;
            })
            .addCase(fetchSubSubCategoriesThunk.rejected, (state, action) => {
                state.subSubCategories.loading = false;
                state.subSubCategories.error =
                    action.payload || "Failed to fetch sub-sub categories";
            })

            // Sub-sub create
            .addCase(createSubSubCategoryThunk.pending, (state) => {
                state.subSubCategories.actionStatus.create.loading = true;
                state.subSubCategories.actionStatus.create.error = null;
                state.subSubCategories.actionStatus.create.success = false;
            })
            .addCase(createSubSubCategoryThunk.fulfilled, (state, action) => {
                state.subSubCategories.actionStatus.create.loading = false;
                state.subSubCategories.actionStatus.create.success = true;
                state.subSubCategories.items = [...state.subSubCategories.items, action.payload];
            })
            .addCase(createSubSubCategoryThunk.rejected, (state, action) => {
                state.subSubCategories.actionStatus.create.loading = false;
                state.subSubCategories.actionStatus.create.error =
                    action.payload || "Failed to create sub-sub category";
                state.subSubCategories.actionStatus.create.success = false;
            })

            // sub-sub update
            .addCase(updateSubSubCategoryThunk.pending, (state) => {
                state.subSubCategories.actionStatus.update.loading = true;
                state.subSubCategories.actionStatus.update.error = null;
                state.subSubCategories.actionStatus.update.success = false;
            })
            .addCase(updateSubSubCategoryThunk.fulfilled, (state, action) => {
                state.subSubCategories.actionStatus.update.loading = false;
                state.subSubCategories.actionStatus.update.success = true;
                state.subSubCategories.items = state.subSubCategories.items.map((item) =>
                    item.id === action.payload.id ? action.payload : item
                );
            })
            .addCase(updateSubSubCategoryThunk.rejected, (state, action) => {
                state.subSubCategories.actionStatus.update.loading = false;
                state.subSubCategories.actionStatus.update.error =
                    action.payload || "Failed to update sub-sub category";
                state.subSubCategories.actionStatus.update.success = false;
            })

            // sub-sub toggle status
            .addCase(toggleSubSubcategoriestatusThunk.pending, (state) => {
                state.subSubCategories.actionStatus.toggle.loading = true;
                state.subSubCategories.actionStatus.toggle.error = null;
                state.subSubCategories.actionStatus.toggle.success = false;
            })
            .addCase(toggleSubSubcategoriestatusThunk.fulfilled, (state, action) => {
                state.subSubCategories.actionStatus.toggle.loading = false;
                state.subSubCategories.actionStatus.toggle.success = true;
                state.subSubCategories.items = state.subSubCategories.items.map((item) =>
                    item.id === action.payload.id
                        ? { ...item, enable: action.payload.enable }
                        : item
                );
            })
            .addCase(toggleSubSubcategoriestatusThunk.rejected, (state, action) => {
                state.subSubCategories.actionStatus.toggle.loading = false;
                state.subSubCategories.actionStatus.toggle.error =
                    action.payload || "Failed to toggle sub-sub category status";
                state.subSubCategories.actionStatus.toggle.success = false;
            })
            // Bulk toggle via parent levels
            .addCase(toggleSubCategoryTreeStatusThunk.pending, (state) => {
                state.subSubCategories.actionStatus.toggle.loading = true;
                state.subSubCategories.actionStatus.toggle.error = null;
                state.subSubCategories.actionStatus.toggle.success = false;
            })
            .addCase(toggleSubCategoryTreeStatusThunk.fulfilled, (state, action) => {
                state.subSubCategories.actionStatus.toggle.loading = false;
                state.subSubCategories.actionStatus.toggle.success = true;
                const { ids, enable } = action.payload;
                state.subSubCategories.items = state.subSubCategories.items.map((item) =>
                    ids.includes(item.id) ? { ...item, enable } : item
                );
            })
            .addCase(toggleSubCategoryTreeStatusThunk.rejected, (state, action) => {
                state.subSubCategories.actionStatus.toggle.loading = false;
                state.subSubCategories.actionStatus.toggle.error =
                    action.payload || "Failed to toggle sub category tree status";
                state.subSubCategories.actionStatus.toggle.success = false;
            })
            .addCase(toggleMainCategoryTreeStatusThunk.pending, (state) => {
                state.subSubCategories.actionStatus.toggle.loading = true;
                state.subSubCategories.actionStatus.toggle.error = null;
                state.subSubCategories.actionStatus.toggle.success = false;
            })
            .addCase(toggleMainCategoryTreeStatusThunk.fulfilled, (state, action) => {
                state.subSubCategories.actionStatus.toggle.loading = false;
                state.subSubCategories.actionStatus.toggle.success = true;
                const { ids, enable } = action.payload;
                state.subSubCategories.items = state.subSubCategories.items.map((item) =>
                    ids.includes(item.id) ? { ...item, enable } : item
                );
            })
            .addCase(toggleMainCategoryTreeStatusThunk.rejected, (state, action) => {
                state.subSubCategories.actionStatus.toggle.loading = false;
                state.subSubCategories.actionStatus.toggle.error =
                    action.payload || "Failed to toggle main category tree status";
                state.subSubCategories.actionStatus.toggle.success = false;
            });
    }
});

export const {
    resetActionStatus,
    resetSubSubCategoriesActionStatus,
    resetMainCategoriesActionStatus,
    resetSubCategoriesActionStatus,
    clearSubCategories
} = categoriesSlice.actions;

export default categoriesSlice.reducer;
